import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { message, Alert, Button, Modal } from 'antd';
import apolloClient from 'helpers/apolloClient';
import autobind from 'autobind-decorator';
import cancelableQuery from 'helpers/apolloClient/cancelableQuery';
import InfiniteScrollableTable from 'components/InfiniteScrollableTable';

import { topupListQuery } from '../UserQueries';
import { revertWithdrawPointMutation } from '../UserMutations';
import { columns } from './UserTopupListMetadata';

@autobind
class UserTopupList extends PureComponent {
  static propTypes = {
    userId: PropTypes.string.isRequired,
  };

  state = {
    data: [],
    error: null,
    loading: false,
    page: 1,
    pageSize: 10,
  };

  componentDidMount() {
    const { page, pageSize } = this.state;
    this.getData(page, pageSize);
  }

  componentWillUnmount() {
    if (this.query) {
      this.query.cancel();
    }
  }

  query = null;

  /**
   * Get Data and append to current data list
   * @param {Number} page - Page to load
   * @param {Number} pageSize - Page size
   */
  async getData(page, pageSize) {
    try {
      this.setState({ loading: true, error: null });

      const { page: oldPage } = this.state;
      const { userId } = this.props;

      this.query = cancelableQuery({
        query: topupListQuery,
        variables: {
          filter: JSON.stringify({
            userId: userId.replace('CS-', ''),
          }),
          skip: (page - 1) * pageSize,
          pageSize,
        },
      });

      const result = await this.query;
      const { list = [] } = result.data.userTopupList;
      const data = list.map(record => ({
        ...record,
        amount: record.type === 'charge' ? record.amount : -record.amount,
      }));

      this.setState({
        loading: false,
        data: page === 1 ? data : [...this.state.data, ...data],
        page: data.length === 0 ? oldPage : page, // Don't update page if there's no data
      });
    } catch (error) {
      if (error.isCanceled) return;

      message.error('Failed to get Topup List');
      this.setState({ error, loading: false });

      throw error;
    }
  }

  async revertWithdrawPoint(topupId) {
    try {
      await apolloClient.mutate({
        mutation: revertWithdrawPointMutation,
        variables: {
          topupId,
        },
      });

      message.success('Successfully Withdrew Cancel');
      this.getData(1, 10);
    } catch (error) {
      message.error(error.message);
    }
  }

  revertWithdrawPointConfirm(topupId) {
    Modal.confirm({
      title: 'Would you like to withdraw cancel?',
      content: 'The recovered balance can be withdrawn again.',
      onOk: this.revertWithdrawPoint.bind(this, topupId),
      onCancel() {},
    });
  }

  getNextPage() {
    const { page, pageSize } = this.state;
    this.getData(page + 1, pageSize);
  }

  render() {
    const { data, error, loading } = this.state;
    const revertWithdrawFn = this.revertWithdrawPointConfirm;

    const statusColumn = {
      title: 'Status',
      dataIndex: 'status',
      render: function renderStatus(status, val) {
        let cancelButton;
        if (status === 'approved') {
          cancelButton = (
            <Button type="danger" ghost icon="dollar" onClick={revertWithdrawFn.bind(this, val.id)}>
              Withdraw Cancel
            </Button>
          );
        }

        return (
          <span>
            {status}
            {cancelButton}
          </span>
        );
      },
      width: '10%',
    };

    return (
      <div>
        {error && (
          <Alert
            description="Failed to load Topup Data."
            type="warning"
            showIcon
            closable
            style={{
              marginBottom: 10,
            }}
          />
        )}

        <InfiniteScrollableTable
          id="user-topup-list-table"
          columns={columns.concat([statusColumn])}
          dataSource={data}
          loading={loading}
          onFetch={this.getNextPage}
        />
      </div>
    );
  }
}

export default UserTopupList;
